// src/features/dashboard/components/AdminTaskSection.tsx

import React from 'react';

import {
  AlertCircle,
  Coins,
  UserRoundSearch,
} from 'lucide-react';

import type {
  AdminTaskSummary,
} from '../types/adminDashboard.types';


type AdminTaskSectionProps = {
  data: AdminTaskSummary;
};


type TaskItemProps = {
  title: string;

  description: string;

  count: number;

  suffix: string;

  icon:
    React.ReactNode;

  emphasis?: boolean;
};


const TaskItem:
  React.FC<
    TaskItemProps
  > = ({
    title,
    description,
    count,
    suffix,
    icon,
    emphasis = false,
  }) => {
    return (
      <div
        className={`
          flex items-center gap-3
          rounded-xl border p-3.5
          ${
            emphasis
              ? 'border-orange-100 bg-orange-50'
              : 'border-gray-100 bg-gray-50'
          }
        `}
      >

        <span
          className={`
            grid h-10 w-10
            shrink-0 place-items-center
            rounded-xl bg-white shadow-sm
            ${
              emphasis
                ? 'text-orange-600'
                : 'text-gray-500'
            }
          `}
        >
          {icon}
        </span>


        <div className="min-w-0 flex-1">

          <p className="text-sm font-semibold text-gray-800">
            {title}
          </p>


          <p className="mt-0.5 truncate text-[11px] text-gray-400">
            {description}
          </p>

        </div>


        <div className="shrink-0 text-right">

          <strong
            className={`
              text-lg font-black
              ${
                emphasis
                  ? 'text-orange-600'
                  : 'text-gray-900'
              }
            `}
          >
            {count.toLocaleString()}
          </strong>


          <span className="ml-1 text-xs text-gray-400">
            {suffix}
          </span>

        </div>

      </div>
    );
  };


const AdminTaskSection:
  React.FC<
    AdminTaskSectionProps
  > = ({
    data,
  }) => {
    const {
      revisitTargetCount,
      pendingPointCount,
    } = data;


    const totalTaskCount =
      revisitTargetCount +
      pendingPointCount;


    return (
      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 sm:px-5">

          <div className="flex items-center gap-3">

            <span className="grid h-10 w-10 place-items-center rounded-xl bg-main/10 text-main">
              <AlertCircle
                size={20}
              />
            </span>


            <div>

              <h2 className="text-base font-bold text-gray-900">
                관리 필요
              </h2>


              <p className="mt-0.5 text-xs text-gray-400">
                오늘 확인이 필요한 운영 항목입니다.
              </p>

            </div>

          </div>


          <span
            className={`
              rounded-full px-2.5 py-1
              text-xs font-bold
              ${
                totalTaskCount >
                0
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-emerald-100 text-emerald-700'
              }
            `}
          >
            {totalTaskCount >
            0
              ? `${totalTaskCount}건`
              : '정상'}
          </span>

        </div>


        {/* Body */}
        <div className="space-y-2.5 p-4 sm:p-5">

          <TaskItem
            title="재방문 관리 고객"
            description="최근 방문이 없어 다시 관리가 필요한 고객"
            count={
              revisitTargetCount
            }
            suffix="명"
            icon={
              <UserRoundSearch
                size={19}
              />
            }
            emphasis={
              revisitTargetCount >
              0
            }
          />


          <TaskItem
            title="포인트 확인 필요"
            description="확인이 필요한 포인트 처리 내역"
            count={
              pendingPointCount
            }
            suffix="건"
            icon={
              <Coins
                size={19}
              />
            }
            emphasis={
              pendingPointCount >
              0
            }
          />

        </div>


        {/* Footer */}
        {totalTaskCount >
        0 ? (

          <div className="border-t border-orange-100 bg-orange-50 px-4 py-3 sm:px-5">

            <p className="text-xs leading-5 text-orange-700">
              확인이 필요한 운영 항목이{' '}
              <strong>
                {totalTaskCount.toLocaleString()}
                건
              </strong>{' '}
              있습니다.
            </p>

          </div>

        ) : (

          <div className="border-t border-emerald-100 bg-emerald-50 px-4 py-3 sm:px-5">

            <p className="text-xs leading-5 text-emerald-700">
              현재 확인이 필요한 운영 항목이 없습니다.
            </p>

          </div>

        )}

      </section>
    );
  };


export default AdminTaskSection;